import { inject, Injectable } from '@angular/core';
import { DialogHelper } from '@common/modules/dialog/dialog.helper';
import { RecordsSandbox } from '@common/modules/records/sandbox/records.sandbox.service';
import { PermanentDeleteActionService } from '@common/sandboxes/actions/action-services/record-actions/permanent-delete/permanent-delete.action.service';
import { MediaHavenActionType } from '@common/sandboxes/actions/entities/action-types.entity';
import { ActionItemOptions, UserAction, UserStoreService } from '@mediahaven/shared';
import { ApiFragment, Mh2Entity, NavigationLink, UserFunctions } from '@stylehaven/components';
import { Observable } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { ActionService } from '../../abstract/action.service.interface';

@Injectable({ providedIn: 'root' })
export class RecoverRecordActionService extends ActionService {
    protected override readonly action: MediaHavenActionType = MediaHavenActionType.RECOVER_RECORD;

    // Services
    private readonly dialog: DialogHelper = inject(DialogHelper);
    private readonly userStoreService: UserStoreService = inject(UserStoreService);
    private readonly recordSandbox: RecordsSandbox = inject(RecordsSandbox);
    private readonly permanentlyDeleteActionService: PermanentDeleteActionService =
        inject(PermanentDeleteActionService);

    // Behaviour
    protected readonly ENABLE_BUSY_TOAST: boolean = false;
    protected readonly REFRESH_ON_ACTION: boolean = true;
    protected readonly DISABLE_ON_EXECUTE: boolean = true;

    /* Restore
     * Available when (linked to PermanentDelete permissions):
     *  * User has delete rights
     *  * User has permanent delete rights
     *  * Item is deleted ('LogicallyDeleted')
     */
    public getUserAction(record: ApiFragment): Observable<UserAction> {
        return this.getUserFunctions().pipe(map(userFunctions => this.userActionBuilder(record, userFunctions)));
    }

    public userActionBuilder(record: ApiFragment, userFunctions: UserFunctions): UserAction {
        return this.permanentlyDeleteActionService.userActionBuilder(record, userFunctions);
    }

    private getUserFunctions(): Observable<UserFunctions> {
        return this.userStoreService.getUserFunctions();
    }

    protected onAction(record: ApiFragment, actionItemOptions: ActionItemOptions): Observable<boolean> {
        const message: string = this.getRecoverDeletedRecordConfirmationMessage(record);
        const title: string = this.translate('actions.recover-record');
        const yes: string = this.translate('actions.recover-record');
        const no: string = this.translate('actions.common.cancel');

        return this.dialog.confirm({ title, message, yes, no }).pipe(
            tap(() => this.initBusyToast(record, actionItemOptions, true)),
            switchMap(confirm => this.recordSandbox.recoverDeletedRecord(record).pipe(take(1))),
            map(() => true)
        );
    }

    private getRecoverDeletedRecordConfirmationMessage(record: ApiFragment): string {
        const entity: Mh2Entity = record.getMh2Entity();
        return this.translate(`items.recover-record-confirm-dialog.${entity}`, { items: 1 });
    }

    // #region Labels

    public override getLabelKey(target: ApiFragment): string {
        return 'actions.recover-record';
    }

    public override getToastLabelKey(target: ApiFragment): string {
        return 'action-toasts.recover-record';
    }

    public override getToastEntity(target: ApiFragment, actionItemOptions: ActionItemOptions): Mh2Entity | string {
        return target.getMh2Entity();
    }

    public override getSuccessToastLinkLabelKey(target: ApiFragment, actionItemOptions: ActionItemOptions): string {
        return 'toasts.see.details';
    }

    public override getSuccessToastLink(
        target: ApiFragment,
        actionItemOptions: ActionItemOptions
    ): NavigationLink | undefined {
        return { route: target.getDetailUrl() };
    }

    public override getToastTranslationOptions(
        target: ApiFragment,
        actionItemOptions: ActionItemOptions
    ): Record<string, string> {
        return {};
    }

    // #endregion
}
